import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Camera, Church } from 'lucide-react';
import { useLongPressShare } from '../components/ShareButton';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

/**
 * Public media (gallery image) detail page (route: /gallery/:id).
 * Tap Share or long-press the image to open the native share sheet with the VOB preview card.
 */
export default function MediaDetailPage() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const longPress = useLongPressShare({
    kind: 'media',
    itemId: id,
    title: item?.title || 'Church Gallery',
    description: item?.description || '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Try media_items first, fall back to webcast_items (homepage gallery)
        let data = null;
        try {
          const res = await axios.get(`${API}/media/${id}`);
          data = res.data;
        } catch {
          const res = await axios.get(`${API}/webcast/items/${id}`);
          data = res.data;
          // Normalise webcast field names
          if (data && !data.file_url) {
            data.file_url = data.photo_url || data.youtube_url || '';
            data.file_type = (data.item_type === 'live' || data.item_type === 'recorded') ? 'video' : 'image';
          }
        }
        if (!cancelled) setItem(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Media not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Resolve relative file paths against the current origin so long-press → "Share image"
  // hands the visitor a cccvob.com URL after deployment.
  const fileSrc = (() => {
    if (!item?.file_url) return '';
    if (item.file_url.startsWith('http://') || item.file_url.startsWith('https://')) {
      return item.file_url;
    }
    if (item.file_url.startsWith('/')) {
      return `${window.location.origin}${item.file_url}`;
    }
    return `${window.location.origin}/${item.file_url}`;
  })();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-900 via-purple-900 to-indigo-900 flex items-center justify-center text-white">
        <div className="animate-pulse">Loading…</div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-900 via-purple-900 to-indigo-900 flex flex-col items-center justify-center text-white px-6">
        <Church className="w-16 h-16 text-amber-300 mb-4" />
        <h1 className="text-2xl font-bold mb-2">Item not found</h1>
        <p className="text-violet-200 mb-6 text-center">This gallery link may have expired or been removed.</p>
        <Link to="/" className="px-6 py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold">
          Visit our church online
        </Link>
      </div>
    );
  }

  const isVideo = (item.file_type || '').toLowerCase() === 'video';

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-900 via-purple-900 to-indigo-900 text-white" data-testid="media-detail-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-sm font-medium" data-testid="media-detail-back">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl bg-black/30 backdrop-blur border border-white/10"
        >
          {isVideo ? (
            <video src={fileSrc} controls poster={item.thumbnail_url || ''} className="w-full max-h-[80vh] bg-black" />
          ) : (
            <img {...longPress} src={fileSrc} alt={item.title} className="w-full max-h-[80vh] object-contain bg-black/40 select-none" draggable />
          )}
          <div className="p-5 sm:p-8">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-violet-100 text-xs font-medium">
                <Camera className="w-3.5 h-3.5" /> CCC VOB Gallery
              </span>
              {item.category && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 text-amber-200 text-xs font-medium capitalize">
                  {item.category}
                </span>
              )}
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold mb-2">{item.title}</h1>
            {item.description && (
              <p className="text-violet-100/90 leading-relaxed whitespace-pre-line">{item.description}</p>
            )}
            {!isVideo && (
              <p className="mt-4 text-xs text-violet-200/60 italic">
                Tip: long-press the image above to share.
              </p>
            )}
          </div>
        </motion.div>

        <div className="text-center mt-8">
          <Link to="/" className="text-amber-200 hover:text-amber-100 text-sm font-medium">
            ← Back to Celestial Church of Christ — Valley of Berachah
          </Link>
        </div>
      </div>
    </div>
  );
}
